import React from "react";
import PropTypes from "prop-types";
import * as R from "ramda";
import { css } from "glamor";
import moment from "moment";
import { Row, Col, Form, Input, Icon, DatePicker, Divider } from "antd";
import Button from "@arachnys/styleguide/Button";
import { Fieldset } from "@arachnys/styleguide/Fieldset";
import SchemaSelect from "@arachnys/styleguide/SchemaSelect";
import { countryOptions } from "../../data";
import { getJsonSchemaFromPath } from "../../util";

const styles = {
    typeField: css({
        width: "240px",
    }),
    iconsContainer: css({
        paddingTop: "37px",
        marginBottom: "20px",
        paddingLeft: "0px !important",
        textAlign: "right",
        "@media(max-width: 1050px)": {
            minWidth: "60px",
            paddingRight: "0 !important",
        },
    }),
    icon: css({
        width: "24px",
        display: "inline-block",
        height: "24px",
        color: "#00aeef",
        fontSize: 24,
        cursor: "pointer",
        marginRight: "13px",
        "@media(max-width: 1245px)": {
            marginRight: "4px",
        },
    }),
    iconSingle: css({
        marginRight: "50px",
        "@media(max-width: 1190px)": {
            marginRight: "32px",
        },
    }),
    button: css({
        margin: "1rem 28px 1rem 0",
        textAlign: "right",
    }),
};

const emptyRelation = () => ({
    type: undefined,
    fromEntity: {
        name: "",
        entityType: undefined,
        legalAddress: {
            streetAddress: undefined,
            city: undefined,
            state: undefined,
            postalCode: undefined,
            countryCode: undefined,
        },
        physicalAddress: {
            streetAddress: undefined,
            city: undefined,
            state: undefined,
            postalCode: undefined,
            countryCode: undefined,
        },
        alternativeNames: {
            localNames: [""],
        },
        additionalProperties: {
            phoneNumber: undefined,
            website: undefined,
            email: undefined,
        },
    },
});

const RelationMainInfo = ({
    relation,
    idPrefix,
    researchRequestSchema,
    disabled,
    getFieldDecorator,
    saveFormInState,
}) => {
    const entityTypeSchema = getJsonSchemaFromPath(
        "entity.entityType",
        researchRequestSchema
    );

    const relationTypeSchema = getJsonSchemaFromPath(
        "relationList.relations.type",
        researchRequestSchema
    );

    const paths = {
        name: ["fromEntity", "name"],
        localLanguageName: ["fromEntity", "alternativeNames", "localNames", 0],
        entityType: ["fromEntity", "entityType"],
        relationType: ["type"],
    };

    return (
        <>
            <Col span={6}>
                <Form.Item label="Entity name">
                    {getFieldDecorator(`${idPrefix}[name]`, {
                        rules: [
                            {
                                required: true,
                                message:
                                    "Please provide the name of this related entity.",
                            },
                        ],
                        initialValue: R.pathOr("", paths.name, relation),
                        getValueProps: saveFormInState(paths.name),
                    })(<Input disabled={disabled} />)}
                </Form.Item>
            </Col>
            <Col span={5}>
                <Form.Item label="Local Language Name">
                    {getFieldDecorator(`${idPrefix}[local_language_name]`, {
                        initialValue: R.path(paths.localLanguageName, relation),
                        getValueProps: saveFormInState(paths.localLanguageName),
                    })(
                        <Input
                            placeholder="Local Language Name"
                            disabled={disabled}
                        />
                    )}
                </Form.Item>
            </Col>

            <Col span={5}>
                <Form.Item label="Entity Type">
                    {getFieldDecorator(`${idPrefix}[entity_type]`, {
                        initialValue: R.path(paths.entityType, relation),
                        rules: [
                            {
                                required: true,
                                message: "Please select an entity type",
                            },
                        ],
                        getValueProps: saveFormInState(paths.entityType),
                    })(
                        <SchemaSelect
                            disabled={disabled}
                            schema={entityTypeSchema}
                        />
                    )}
                </Form.Item>
            </Col>

            <Col span={6}>
                <Form.Item label="Relation Type">
                    {getFieldDecorator(`${idPrefix}[relation_type]`, {
                        initialValue: R.path(paths.relationType, relation),
                        rules: [
                            {
                                required: true,
                                message: "Please select a relation type",
                            },
                        ],
                        getValueProps: saveFormInState(paths.relationType),
                    })(
                        <SchemaSelect
                            disabled={disabled}
                            schema={relationTypeSchema}
                        />
                    )}
                </Form.Item>
            </Col>
        </>
    );
};

const RelationMiscInfo = ({
    relation,
    idPrefix,
    disabled,
    getFieldDecorator,
    saveFormInState,
}) => {
    const properties = ["fromEntity", "additionalProperties"];

    const paths = {
        idNumber: [...properties, "idNumber"],
        phoneNumber: [...properties, "phoneNumber"],
        email: [...properties, "email"],
        website: [...properties, "website"],
        dateOfBirth: ["fromEntity", "dateOfBirth"],
    };

    return (
        <Col span={11}>
            <Form.Item label="Date of Incorporation/Birth">
                {getFieldDecorator(`${idPrefix}[date_of_birth]`, {
                    initialValue: R.pipe(
                        R.path(paths.dateOfBirth),
                        R.unless(R.either(R.isNil, R.isEmpty), moment)
                    )(relation),
                    getValueProps: saveFormInState(paths.dateOfBirth),
                })(
                    <DatePicker
                        placeholder="Select date of birth"
                        disabled={disabled}
                    />
                )}
            </Form.Item>
            <Form.Item label="Phone number">
                {getFieldDecorator(`${idPrefix}[phone_number]`, {
                    initialValue: R.path(paths.phoneNumber, relation),
                    getValueProps: saveFormInState(paths.phoneNumber),
                })(<Input placeholder="Phone Number" disabled={disabled} />)}
            </Form.Item>
            <Form.Item label="Email">
                {getFieldDecorator(`${idPrefix}[email]`, {
                    initialValue: R.path(paths.email, relation),
                    getValueProps: saveFormInState(paths.email),
                })(<Input placeholder="Email" disabled={disabled} />)}
            </Form.Item>
            <Form.Item label="Website">
                {getFieldDecorator(`${idPrefix}[website]`, {
                    initialValue: R.path(paths.website, relation),
                    getValueProps: saveFormInState(paths.website),
                })(<Input placeholder="Website" disabled={disabled} />)}
            </Form.Item>
            <Form.Item label="ID Number">
                {getFieldDecorator(`${idPrefix}[id_number]`, {
                    initialValue: R.path(paths.idNumber, relation),
                    getValueProps: saveFormInState(paths.idNumber),
                })(<Input placeholder="ID Number" disabled={disabled} />)}
            </Form.Item>
        </Col>
    );
};

const getAddressFieldName = entity => {
    const entityType = entity.entityType;

    return entityType === "INDIVIDUAL" ? "physicalAddress" : "legalAddress";
};

const RelationAddress = ({
    relation,
    idPrefix,
    disabled,
    getFieldDecorator,
    saveFormInState,
}) => {
    const addressField = getAddressFieldName(
        R.propOr({}, "fromEntity", relation)
    );

    const countrySchema = { options: countryOptions };

    const address = ["fromEntity", addressField];

    const paths = {
        streetAddress: [...address, "streetAddress"],
        city: [...address, "city"],
        state: [...address, "state"],
        postalCode: [...address, "postalCode"],
        country: [...address, "countryCode"],
    };

    return (
        <Col span={11}>
            <Form.Item label="Street Address">
                {getFieldDecorator(`${idPrefix}[street_address]`, {
                    initialValue: R.path(paths.streetAddress, relation),
                    getValueProps: saveFormInState(paths.streetAddress),
                })(<Input placeholder="Street Address" disabled={disabled} />)}
            </Form.Item>

            <Form.Item label="City">
                {getFieldDecorator(`${idPrefix}[city]`, {
                    initialValue: R.path(paths.city, relation),
                    getValueProps: saveFormInState(paths.city),
                })(<Input placeholder="City" disabled={disabled} />)}
            </Form.Item>

            <Form.Item label="State">
                {getFieldDecorator(`${idPrefix}[state]`, {
                    initialValue: R.path(paths.state, relation),
                    getValueProps: saveFormInState(paths.state),
                })(<Input placeholder="State" disabled={disabled} />)}
            </Form.Item>

            <Form.Item label="Postal Code">
                {getFieldDecorator(`${idPrefix}[postal_code]`, {
                    initialValue: R.path(paths.postalCode, relation),
                    getValueProps: saveFormInState(paths.postalCode),
                })(<Input placeholder="Postal Code" disabled={disabled} />)}
            </Form.Item>
            <Form.Item label="Country">
                {getFieldDecorator(`${idPrefix}[country_code]`, {
                    initialValue: R.path(paths.country, relation),
                    rules: [
                        {
                            required: true,
                            message:
                                "Please provide the country of this related entity",
                        },
                    ],
                    getValueProps: saveFormInState(paths.country),
                })(
                    <SchemaSelect
                        placeholder="Country"
                        showSearch
                        disabled={disabled}
                        schema={countrySchema}
                    />
                )}
            </Form.Item>
        </Col>
    );
};

class EntityRelations extends React.Component {
    constructor(props) {
        super(props);

        const relations = R.pathOr([], ["researchRequest", "relations"], props);

        this.state = { relations };

        this.relationsCache = R.clone(relations);
    }

    remove = indexToRemove => () => {
        this.relationsCache = R.remove(indexToRemove, 1, this.relationsCache);
        // TODO: dirty hack vs uniforms. remove when uniforms will be removed
        this.setState(
            {
                relations: [],
            },
            () => {
                this.setState({
                    relations: this.relationsCache,
                });
            }
        );
    };

    add = () => {
        const relations = [...this.state.relations, emptyRelation()];
        this.relationsCache = relations;
        this.setState({ relations });
    };

    generateIcons = (isLastRelation, key) => {
        const minusStyle = isLastRelation
            ? styles.icon
            : { ...styles.icon, ...styles.iconSingle };
        return (
            <Col span={2} {...styles.iconsContainer}>
                <Icon
                    type="minus-circle"
                    onClick={this.remove(key)}
                    {...minusStyle}
                />
                {isLastRelation && (
                    <Icon
                        type="plus-circle"
                        onClick={this.add}
                        {...styles.icon}
                    />
                )}
            </Col>
        );
    };

    saveFormInState = index => path => value => {
        this.relationsCache = R.assocPath(
            [index, ...path],
            value,
            this.relationsCache
        );
        return { value };
    };

    render() {
        const { relations } = this.state;
        const {
            schemas: { researchRequestSchema },
            form: { getFieldDecorator },
        } = this.props;

        if (relations.length === 0) {
            return (
                <div {...styles.button}>
                    <Button.Primary onClick={this.add}>
                        Add relation
                    </Button.Primary>
                </div>
            );
        }

        const rows = this.relationsCache.map((rel, index) => {
            const disabled = R.is(Object, this.props.disabled)
                ? this.props.disabled.relations
                : this.props.disabled;

            const idPrefix = `relation_list[${index}]`;

            const isLast = index === relations.length - 1;
            const saveFormInState = this.saveFormInState(index);
            return (
                <React.Fragment key={index}>
                    <Row gutter={40} type="flex" align="middle">
                        <RelationMainInfo
                            idPrefix={idPrefix}
                            relation={rel}
                            researchRequestSchema={researchRequestSchema}
                            disabled={disabled}
                            getFieldDecorator={getFieldDecorator}
                            saveFormInState={saveFormInState}
                        />
                        <RelationMiscInfo
                            idPrefix={idPrefix}
                            relation={rel}
                            disabled={disabled}
                            getFieldDecorator={getFieldDecorator}
                            saveFormInState={saveFormInState}
                        />
                        <RelationAddress
                            idPrefix={idPrefix}
                            relation={rel}
                            disabled={disabled}
                            getFieldDecorator={getFieldDecorator}
                            saveFormInState={saveFormInState}
                        />
                        {!disabled && this.generateIcons(isLast, index)}
                    </Row>
                    {isLast || <Divider />}
                </React.Fragment>
            );
        });

        return <Fieldset>{rows}</Fieldset>;
    }
}

EntityRelations.propTypes = {
    form: PropTypes.object,
    researchRequest: PropTypes.object,
    disable: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    // json schemas for entity and relation to use for
    // producing the form options
    schemas: PropTypes.object,
};
export default EntityRelations;
